import React, { useRef } from 'react';
import { styled } from '@mui/material/styles';
import Paper from '@mui/material/Paper';
import { Avatar, Button, Grid, IconButton, Stack, Typography, createTheme, useMediaQuery } from '@mui/material';
import {
  AddCircleOutline,
  CloudDownload,
  CloudUpload,
  DeleteForever,
  DraftsOutlined,
  Edit,
  Person,
} from '@mui/icons-material';
import { EditLeadDetails } from '../EditLeadDetails';
import { ReadOnlyLeadDetails } from '../ReadOnlyLeadDetails';
import { datetimeFormatter } from '../../../utils/datetimeFormatter';
import DocumentStore from '../../../store/DocumentStore';
import AuthStore from '../../../store/AuthStore';
import { EditClientInfo } from '../EditClientInfo';
import { ReadOnlyClientInfo } from '../ReadOnlyClientInfo';
import { calculateCompletionPercentage } from '../../../utils/helper';
import { documentType } from '../../../utils/documentType';
import FileIcon from '../../../assets/images/FileIcon.png';
import BorderLinearProgress from '../../common/BorderLinearProgress';

const CustomPaper = styled(Paper)({
  padding: '30px',
});

const CustomUploadButton = styled(Button)({
  backgroundColor: '#F07B0E',
  color: '#fff',
  '&:hover': {
    backgroundColor: '#f1785a',
  },
  width: 'fit-content',
});

const CustomStack = styled(Stack)({
  '&:hover': {
    backgroundColor: '#ecf0f4',
    cursor: 'pointer',
    boxShadow: '0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12)',
  },
});

const SmallDownloadAvatar = styled(Avatar)(({ theme }) => ({
  width: 30,
  height: 30,
  border: `2px solid ${theme.palette.background.paper}`,
  backgroundColor: '#F07B0E',
}));

const SmallDeleteAvatar = styled(Avatar)(({ theme }) => ({
  width: 30,
  height: 30,
  border: `2px solid ${theme.palette.background.paper}`,
  backgroundColor: '#d32f2f',
}));

const SmallEditAvatar = styled(Avatar)(({ theme }) => ({
  width: 30,
  height: 30,
  border: `2px solid ${theme.palette.background.paper}`,
  backgroundColor: '#F07B0E',
}));

const CommonBottomSection = ({
  leadDetails,
  setUploadFilesDocumentType,
  handleDownload,
  handlePreviewFile,
  handleRemoveDocument,
  handleToggleLeadActionDialog,
  handleToggleLeadCallDialog,
  handleToggleLeadPaymentAuditDialog,
  editClientInfo,
  handleEditClientInfo,
}) => {
  const theme = createTheme();
  const clientInfoRef = useRef(null);
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const CustomButton = styled(Button)({
    backgroundColor: '#F07B0E',
    color: '#fff',
    '&:hover': {
      backgroundColor: '#f1785a',
    },
    width: 'fit-content',
    alignSelf: isSmallScreen ? 'center' : 'end',
  });

  return (
    <>
      {/* Client Details: Document Section */}
      <Stack>
        <Paper>
          <Stack
            direction={'row'}
            sx={{
              borderBottom: '1px solid #c7c7c7',
              px: '30px',
              py: '20px',
              backgroundColor: '#cce6e6',
            }}
          >
            <Stack>
              <Typography sx={{ fontWeight: '600' }} component="p">
                Lead Supporting Documents
              </Typography>
            </Stack>
            <Stack sx={{ ml: 'auto' }}>
              <CustomUploadButton
                variant="contained"
                title="You can upload max of 2 files"
                tabIndex={-1}
                size="small"
                onClick={() => setUploadFilesDocumentType(documentType.leadSupportingDocuments)}
                startIcon={<CloudUpload />}
              >
                Upload files
              </CustomUploadButton>
            </Stack>
          </Stack>
          {DocumentStore.documentsListByLead?.filter(
            (item) => item.documentType === documentType.leadSupportingDocuments.toString()
          )?.length ? (
            <div id="supporting-docuemnts-container">
              {DocumentStore.documentsListByLead
                ?.filter((item) => item.documentType === documentType.leadSupportingDocuments.toString())
                .map((doc) => {
                  return (
                    <Paper key={doc.id}>
                      <Stack spacing={1} alignItems={'center'} sx={{ height: '100%' }}>
                        <CustomStack
                          flexGrow={1}
                          justifyContent={'center'}
                          className="custom-stack"
                          sx={{ width: '-webkit-fill-available' }}
                          onClick={() => handlePreviewFile(doc)}
                        >
                          <Stack justifyContent={'center'} alignSelf={'center'}>
                            <img
                              style={{
                                width: '100%',
                                flexGrow: '1',
                                height: '100px',
                              }}
                              src={
                                doc.documentExtension === '.pdf'
                                  ? doc.imageBase64
                                  : doc.documentExtension.match(/\.(jpg|jpeg|png|gif|JPG|JPEG|PNG|GIF|webp)$/)
                                    ? doc.documentRefUrl
                                    : FileIcon
                              }
                              alt={doc.documentName}
                            ></img>
                          </Stack>
                        </CustomStack>
                        {AuthStore.userDetails.Role.toUpperCase() === 'SUAD' && (
                          <IconButton
                            sx={{
                              position: 'absolute',
                              marginLeft: '105px !important',
                              marginTop: '-15px !important',
                            }}
                            className="download-file"
                            onClick={() => handleRemoveDocument(doc.id)}
                          >
                            <SmallDeleteAvatar alt="Remy Sharp">
                              <DeleteForever sx={{ height: '22px', width: '22px' }}></DeleteForever>
                            </SmallDeleteAvatar>
                          </IconButton>
                        )}
                        <IconButton
                          sx={{
                            position: 'absolute',
                            marginLeft: '105px !important',
                            marginTop: '20px !important',
                          }}
                          className="delete-file"
                          onClick={() => handleDownload(doc)}
                        >
                          <SmallDownloadAvatar alt="Remy Sharp">
                            <CloudDownload sx={{ height: '20px', width: '20px' }}></CloudDownload>
                          </SmallDownloadAvatar>
                        </IconButton>
                        <Typography sx={{ fontWeight: '300', fontSize: '12px', p: '5px' }} component="p">
                          {doc.documentName}
                        </Typography>
                      </Stack>
                    </Paper>
                  );
                })}
            </div>
          ) : (
            <Typography
              sx={{
                fontWeight: '500',
                alignSelf: 'center',
                fontSize: '16px',
                textAlign: 'center',
                p: '20px',
              }}
              component="p"
            >
              No documents available
            </Typography>
          )}
        </Paper>
      </Stack>
      {/* Client Details: User Document Section */}
      <Stack>
        <Paper>
          <Stack
            direction={'row'}
            sx={{
              borderBottom: '1px solid #c7c7c7',
              px: '30px',
              py: '20px',
              backgroundColor: '#cce6e6',
            }}
          >
            <Stack>
              <Typography sx={{ fontWeight: '600' }} component="p">
                User Documents
              </Typography>
            </Stack>
            <Stack sx={{ ml: 'auto' }}>
              <CustomUploadButton
                variant="contained"
                tabIndex={-1}
                size="small"
                onClick={() => setUploadFilesDocumentType(documentType.userDocuments)}
                startIcon={<CloudUpload />}
              >
                Upload files
              </CustomUploadButton>
            </Stack>
          </Stack>
          {DocumentStore.documentsListByLead?.filter(
            (item) => item.documentType === documentType.userDocuments.toString()
          ).length ? (
            <div id="supporting-docuemnts-container">
              {DocumentStore.documentsListByLead
                ?.filter((item) => item.documentType === documentType.userDocuments.toString())
                .map((doc) => {
                  return (
                    <Paper key={doc.id}>
                      <Stack spacing={1} alignItems={'center'} sx={{ height: '100%' }}>
                        <CustomStack
                          flexGrow={1}
                          justifyContent={'center'}
                          className="custom-stack"
                          sx={{ width: '-webkit-fill-available' }}
                          onClick={() => handlePreviewFile(doc)}
                        >
                          <Stack justifyContent={'center'} alignSelf={'center'}>
                            <img
                              style={{
                                width: '100%',
                                flexGrow: '1',
                                height: '100px',
                              }}
                              src={doc.documentExtension === '.pdf' ? doc.imageBase64 : doc.documentRefUrl}
                              alt={doc.documentName}
                            ></img>
                          </Stack>
                        </CustomStack>
                        {AuthStore.userDetails.Role.toUpperCase() === 'SUAD' && (
                          <IconButton
                            sx={{
                              position: 'absolute',
                              marginLeft: '105px !important',
                              marginTop: '-15px !important',
                            }}
                            className="download-file"
                            onClick={() => handleRemoveDocument(doc.id)}
                          >
                            <SmallDeleteAvatar alt="Remy Sharp">
                              <DeleteForever sx={{ height: '22px', width: '22px' }}></DeleteForever>
                            </SmallDeleteAvatar>
                          </IconButton>
                        )}
                        <IconButton
                          sx={{
                            position: 'absolute',
                            marginLeft: '105px !important',
                            marginTop: '20px !important',
                          }}
                          className="delete-file"
                          onClick={() => handleDownload(doc)}
                        >
                          <SmallDownloadAvatar alt="Remy Sharp">
                            <CloudDownload sx={{ height: '20px', width: '20px' }}></CloudDownload>
                          </SmallDownloadAvatar>
                        </IconButton>
                        <Typography sx={{ fontWeight: '300', fontSize: '12px', p: '5px' }} component="p">
                          {doc.documentName}
                        </Typography>
                      </Stack>
                    </Paper>
                  );
                })}
            </div>
          ) : (
            <Typography
              sx={{
                fontWeight: '500',
                alignSelf: 'center',
                fontSize: '16px',
                textAlign: 'center',
                p: '20px',
              }}
              component="p"
            >
              No documents available
            </Typography>
          )}
        </Paper>
      </Stack>
      {/* Client Details: Site Document Section */}
      <Stack>
        <Paper>
          <Stack
            direction={'row'}
            sx={{
              borderBottom: '1px solid #c7c7c7',
              px: '30px',
              py: '20px',
              backgroundColor: '#cce6e6',
            }}
          >
            <Stack>
              <Typography sx={{ fontWeight: '600' }} component="p">
                Site Documents
              </Typography>
            </Stack>
            <Stack sx={{ ml: 'auto' }}>
              <CustomUploadButton
                variant="contained"
                tabIndex={-1}
                size="small"
                onClick={() => setUploadFilesDocumentType(documentType.siteDocuments)}
                startIcon={<CloudUpload />}
              >
                Upload files
              </CustomUploadButton>
            </Stack>
          </Stack>
          {DocumentStore.documentsListByLead?.filter(
            (item) => item.documentType === documentType.siteDocuments.toString()
          ).length ? (
            <div id="supporting-docuemnts-container">
              {DocumentStore.documentsListByLead
                ?.filter((item) => item.documentType === documentType.siteDocuments.toString())
                .map((doc) => {
                  return (
                    <Paper key={doc.id}>
                      <Stack spacing={1} alignItems={'center'} sx={{ height: '100%' }}>
                        <CustomStack
                          flexGrow={1}
                          justifyContent={'center'}
                          className="custom-stack"
                          sx={{ width: '-webkit-fill-available' }}
                          onClick={() => handlePreviewFile(doc)}
                        >
                          <Stack justifyContent={'center'} alignSelf={'center'}>
                            <img
                              style={{
                                width: '100%',
                                flexGrow: '1',
                                height: '100px',
                              }}
                              src={doc.documentExtension === '.pdf' ? doc.imageBase64 : doc.documentRefUrl}
                              alt={doc.documentName}
                            ></img>
                          </Stack>
                        </CustomStack>
                        {AuthStore.userDetails.Role.toUpperCase() === 'SUAD' && (
                          <IconButton
                            sx={{
                              position: 'absolute',
                              marginLeft: '105px !important',
                              marginTop: '-15px !important',
                            }}
                            className="download-file"
                            onClick={() => handleRemoveDocument(doc.id)}
                          >
                            <SmallDeleteAvatar alt="Remy Sharp">
                              <DeleteForever sx={{ height: '22px', width: '22px' }}></DeleteForever>
                            </SmallDeleteAvatar>
                          </IconButton>
                        )}
                        <IconButton
                          sx={{
                            position: 'absolute',
                            marginLeft: '105px !important',
                            marginTop: '20px !important',
                          }}
                          className="delete-file"
                          onClick={() => handleDownload(doc)}
                        >
                          <SmallDownloadAvatar alt="Remy Sharp">
                            <CloudDownload sx={{ height: '20px', width: '20px' }}></CloudDownload>
                          </SmallDownloadAvatar>
                        </IconButton>
                        <Typography sx={{ fontWeight: '300', fontSize: '12px', p: '5px' }} component="p">
                          {doc.documentName}
                        </Typography>
                      </Stack>
                    </Paper>
                  );
                })}
            </div>
          ) : (
            <Typography
              sx={{
                fontWeight: '500',
                alignSelf: 'center',
                fontSize: '16px',
                textAlign: 'center',
                p: '20px',
              }}
              component="p"
            >
              No documents available
            </Typography>
          )}
        </Paper>
      </Stack>
      {/* Client Details: Additional Service Required Section */}
      <Stack>
        <Paper>
          <Stack
            direction={'row'}
            sx={{
              borderBottom: '1px solid #c7c7c7',
              px: '30px',
              py: '20px',
              backgroundColor: '#cce6e6',
            }}
          >
            <Typography sx={{ fontWeight: '600', alignSelf: 'start' }} component="p">
              Open Actions
            </Typography>
            <CustomButton
              sx={{ ml: 'auto' }}
              variant="contained"
              size="small"
              startIcon={<AddCircleOutline />}
              onClick={() => handleToggleLeadActionDialog(true, null)}
            >
              Add Action
            </CustomButton>
          </Stack>
          {leadDetails.leadActions.length ? (
            <div id="lead-actions-container">
              {leadDetails.leadActions?.map((item, index) => {
                return (
                  <Paper key={index}>
                    <Stack
                      direction={'row'}
                      sx={{
                        borderBottom: '1px solid #c7c7c782',
                        backgroundColor: '#c7c7c782',
                        px: '20px',
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight: '600',
                          alignSelf: 'start',
                          pt: '10px',
                        }}
                        component="p"
                      >
                        {item.name}
                      </Typography>
                      <IconButton sx={{ ml: 'auto' }}>
                        <SmallEditAvatar alt="Remy Sharp" onClick={() => handleToggleLeadActionDialog(true, item)}>
                          <Edit sx={{ height: '16px', width: '16px' }}></Edit>
                        </SmallEditAvatar>
                      </IconButton>
                    </Stack>
                    <Stack direction={'row'} sx={{ p: '20px' }} spacing={2} alignItems={'center'}>
                      <Stack>
                        <Avatar sx={{ width: 30, height: 30 }}>
                          <Person></Person>
                        </Avatar>
                      </Stack>
                      <Stack direction={'column'}>
                        <Typography
                          sx={{
                            fontWeight: '500',
                            alignSelf: 'start',
                            color: '#1D76DE',
                          }}
                          component="p"
                        >
                          {item.description}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '12px',
                            fontWeight: '500',
                            alignSelf: 'start',
                            color: '#626A72',
                          }}
                          component="p"
                        >
                          {item.createdDate
                            ? datetimeFormatter.formatDate(item.createdDate, datetimeFormatter.toLocal)
                            : ''}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '14px',
                            fontWeight: '500',
                            alignSelf: 'start',
                            color: '#626A72',
                          }}
                          component="p"
                        >
                          Action By - {item.userName}
                        </Typography>
                      </Stack>
                    </Stack>
                  </Paper>
                );
              })}
            </div>
          ) : (
            <Typography
              sx={{
                fontWeight: '500',
                alignSelf: 'center',
                fontSize: '16px',
                textAlign: 'center',
                p: '20px',
              }}
              component="p"
            >
              No actions available
            </Typography>
          )}
        </Paper>
      </Stack>
      {/* Client Details: Call Reminders Section */}
      <Stack>
        <Paper>
          <Stack
            direction={'row'}
            sx={{
              borderBottom: '1px solid #c7c7c7',
              px: '30px',
              py: '20px',
              backgroundColor: '#cce6e6',
            }}
          >
            <Typography sx={{ fontWeight: '600', alignSelf: 'start' }} component="p">
              Call Reminders
            </Typography>
            <CustomButton
              sx={{ ml: 'auto' }}
              variant="contained"
              size="small"
              startIcon={<AddCircleOutline />}
              onClick={() => handleToggleLeadCallDialog(true, null)}
            >
              Add Call
            </CustomButton>
          </Stack>
          {leadDetails.leadCallDtos?.length ? (
            <div id="lead-calls-container">
              {leadDetails.leadCallDtos?.map((item, index) => {
                return (
                  <Paper key={index} sx={{ width: '100%', height: '100%' }}>
                    <Stack
                      direction={'row'}
                      sx={{
                        borderBottom: '1px solid #c7c7c782',
                        backgroundColor: '#c7c7c782',
                        px: '20px',
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight: '600',
                          alignSelf: 'start',
                          pt: '10px',
                        }}
                        component="p"
                      >
                        {item.name}
                      </Typography>
                      <IconButton sx={{ ml: 'auto' }}>
                        <SmallEditAvatar alt="Remy Sharp" onClick={() => handleToggleLeadCallDialog(true, item)}>
                          <Edit sx={{ height: '16px', width: '16px' }}></Edit>
                        </SmallEditAvatar>
                      </IconButton>
                    </Stack>
                    <Stack direction={'row'} sx={{ p: '20px' }} spacing={2} alignItems={'center'}>
                      <Stack>
                        <Avatar sx={{ width: 30, height: 30 }}>
                          <Person></Person>
                        </Avatar>
                      </Stack>
                      <Stack direction={'column'}>
                        <Typography
                          sx={{
                            fontWeight: '500',
                            alignSelf: 'start',
                            color: '#1D76DE',
                          }}
                          component="p"
                        >
                          {item.description}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '12px',
                            fontWeight: '500',
                            alignSelf: 'start',
                            color: '#626A72',
                          }}
                          component="p"
                        >
                          {item.callReminderDate
                            ? datetimeFormatter.formatDateTime(item.callReminderDate, datetimeFormatter.toLocal)
                            : ''}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '14px',
                            fontWeight: '500',
                            alignSelf: 'start',
                            color: '#626A72',
                          }}
                          component="p"
                        >
                          Action By - {item.userName}
                        </Typography>
                      </Stack>
                    </Stack>
                  </Paper>
                );
              })}
            </div>
          ) : (
            <Typography
              sx={{
                fontWeight: '500',
                alignSelf: 'center',
                fontSize: '16px',
                textAlign: 'center',
                p: '20px',
              }}
              component="p"
            >
              No calls available
            </Typography>
          )}
        </Paper>
      </Stack>
      {/* Client Details: Payment Audit Section */}
      <Stack>
        <Paper>
          <Stack
            direction={'row'}
            sx={{
              borderBottom: '1px solid #c7c7c7',
              px: '30px',
              py: '20px',
              backgroundColor: '#cce6e6',
            }}
          >
            <Typography sx={{ fontWeight: '600', alignSelf: 'start' }} component="p">
              Payment Audit
            </Typography>
            <CustomButton
              sx={{ ml: 'auto' }}
              variant="contained"
              size="small"
              startIcon={<AddCircleOutline />}
              onClick={() => handleToggleLeadPaymentAuditDialog(true, null)}
            >
              Add Payment Audit
            </CustomButton>
          </Stack>
          {leadDetails.leadPaymentAuditDtos?.length ? (
            <div id="lead-calls-container">
              {leadDetails.leadPaymentAuditDtos?.map((item, index) => {
                return (
                  <Paper key={index} sx={{ width: '100%', height: '100%' }}>
                    <Stack
                      direction={'row'}
                      sx={{
                        borderBottom: '1px solid #c7c7c782',
                        backgroundColor: '#c7c7c782',
                        px: '20px',
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight: '600',
                          alignSelf: 'start',
                          pt: '10px',
                        }}
                        component="p"
                      >
                        {item.name}
                      </Typography>
                      <IconButton sx={{ ml: 'auto' }}>
                        <SmallEditAvatar
                          alt="Remy Sharp"
                          onClick={() => handleToggleLeadPaymentAuditDialog(true, item)}
                        >
                          <Edit sx={{ height: '16px', width: '16px' }}></Edit>
                        </SmallEditAvatar>
                      </IconButton>
                    </Stack>
                    <Stack direction={'row'} sx={{ p: '20px' }} spacing={2} alignItems={'center'}>
                      <Stack>
                        <Avatar sx={{ width: 30, height: 30 }}>
                          <Person></Person>
                        </Avatar>
                      </Stack>
                      <Stack direction={'column'}>
                        <Typography
                          sx={{
                            fontWeight: '500',
                            alignSelf: 'start',
                            color: '#1D76DE',
                          }}
                          component="p"
                        >
                          {item.description}
                        </Typography>
                        {item.paymentStatus && (
                          <Typography
                            sx={{
                              fontSize: '12px',
                              fontWeight: '500',
                              alignSelf: 'start',
                              color: '#626A72',
                            }}
                            component="p"
                          >
                            Amont: {item.paymentStatus}
                          </Typography>
                        )}
                        {item.paymentMode && (
                          <Typography
                            sx={{
                              fontSize: '12px',
                              fontWeight: '500',
                              alignSelf: 'start',
                              color: '#626A72',
                            }}
                            component="p"
                          >
                            Mode: {item.paymentMode}
                          </Typography>
                        )}
                        <Typography
                          sx={{
                            fontSize: '12px',
                            fontWeight: '500',
                            alignSelf: 'start',
                            color: '#626A72',
                          }}
                          component="p"
                        >
                          {item.paymentDate
                            ? datetimeFormatter.formatDateTime(item.paymentDate, datetimeFormatter.toLocal)
                            : ''}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '14px',
                            fontWeight: '500',
                            alignSelf: 'start',
                            color: '#626A72',
                          }}
                          component="p"
                        >
                          Action By - {item.userName}
                        </Typography>
                      </Stack>
                    </Stack>
                  </Paper>
                );
              })}
            </div>
          ) : (
            <Typography
              sx={{
                fontWeight: '500',
                alignSelf: 'center',
                fontSize: '16px',
                textAlign: 'center',
                p: '20px',
              }}
              component="p"
            >
              No Payment Transactions Yet
            </Typography>
          )}
        </Paper>
      </Stack>
      {/* Client Details: Client Information & Contact Details Section */}
      {/* <Stack>
        <Paper>
          <Stack
            direction={'row'}
            sx={{
              borderBottom: '1px solid #c7c7c7',
              px: '30px',
              py: '20px',
              backgroundColor: '#cce6e6',
            }}
          >
            <Typography sx={{ fontWeight: '600', alignSelf: 'start' }} component="p">
              Client Information & Contact Details
            </Typography>
            {editClientInfo ? (
              ''
            ) : (
              <CustomButton sx={{ ml: 'auto' }} variant="contained" size="small" onClick={handleEditClientInfo}>
                Edit
              </CustomButton>
            )}
          </Stack>
          <BorderLinearProgress
            color="primary"
            variant="determinate"
            value={calculateCompletionPercentage(leadDetails, [
              'clientName',
              'clientPointOfContact',
              'clientResidingAddress1',
              'primaryContactNumber',
            ])}
          />
          {editClientInfo ? (
            <EditClientInfo
              clientInfo={leadDetails}
              leadId={leadDetails.id}
              handleEditClientInfo={handleEditClientInfo}
            ></EditClientInfo>
          ) : (
            <ReadOnlyClientInfo ref={clientInfoRef} clientInfo={leadDetails}></ReadOnlyClientInfo>
          )}
        </Paper>
      </Stack> */}
    </>
  );
};

export default CommonBottomSection;
